import loc, { type LocalizedString, type ResourceId } from 'owa-localize';
import { executePrompt } from './executePrompt';
import { executePromptWithQueryAnnotation } from './executePromptWithQueryAnnotation';
import {
    COMPOSE_NEW_HELP_WRITE_CMM_ID,
    COMPOSE_NEW_FIND_RECIPIENTS_CMM_ID,
    COMPOSE_NEW_SUPPORT_STATEMENT_CMM_ID,
    COMPOSE_REPLY_HELP_REPLY_CMM_ID,
    COMPOSE_REPLY_NEXT_STEPS_CMM_ID,
    COMPOSE_REPLY_KEY_POINTS_CMM_ID,
    COMPOSE_FORWARD_EXPLAIN_CMM_ID,
    COMPOSE_FORWARD_WITH_CONTEXT_CMM_ID,
    COMPOSE_FORWARD_WITH_TASKS_CMM_ID,
} from 'owa-bizchat-shared/lib/utils/ScenarioCMMIds';
import {
    composeNewHelpWriteCommandText,
    composeNewHelpWriteDisplayText,
    composeNewFindRecipientsCommandText,
    composeNewFindRecipientsDisplayText,
    composeNewSupportStatementCommandText,
    composeNewSupportStatementDisplayText,
    composeReplyHelpReplyCommandText,
    composeReplyHelpReplyDisplayText,
    composeReplyNextStepsCommandText,
    composeReplyNextStepsDisplayText,
    composeReplyKeyPointsCommandText,
    composeReplyKeyPointsDisplayText,
    composeForwardExplainCommandText,
    composeForwardExplainDisplayText,
    composeForwardWithContextCommandText,
    composeForwardWithContextDisplayText,
    composeForwardWithTasksCommandText,
    composeForwardWithTasksDisplayText,
    showTopActionItemsCommandText,
    showTopActionItemsDisplayText,
    findQuickWinsCommandText,
    findQuickWinsDisplayText,
    getOrientedCommandText,
    getOrientedDisplayText,
    latestOnTopicCommandText,
    latestOnTopicDisplayText,
    needsUrgentRepliesCommandText,
    needsUrgentRepliesDisplayText,
    showKeyTakeawaysCommandText,
    showKeyTakeawaysDisplayText,
    showMyTopThreeEmailsCommandText,
    showMyTopThreeEmailsDisplayText,
    showRelatedResourcesCommandText,
    showRelatedResourcesDisplayText,
    suggestNextStepsCommandText,
    suggestNextStepsDisplayText,
    summarizeAndReplyCommandText,
    summarizeAndReplyDisplayText,
    summarizeCommandText,
    summarizeDisplayText,
    triageMyInboxCommandText,
    triageMyInboxDisplayText,
    whatToGetToFirstCommandText,
    whatToGetToFirstDisplayText,
} from './suggestionCatalog.locstring.json';
import type { MailboxInfo } from 'owa-client-types';
import { isDraftAssistantEnabled } from 'owa-copilot-settings-utils/lib/utils/isDraftAssistantEnabled';

type BaseSuggestion = {
    /** Unique identifier */
    id: string;
    /** Callback function to execute when the suggestion is clicked */
    onClick: (targetWindow: Window, mailboxInfo: MailboxInfo) => void;
};

function executePromptWithAgent(
    promptText: LocalizedString,
    formCode: string,
    targetWindow: Window,
    mailboxInfo: MailboxInfo
) {
    executePromptWithQueryAnnotation(
        promptText,
        formCode,
        targetWindow,
        mailboxInfo,
        undefined,
        isDraftAssistantEnabled(mailboxInfo) ? 'DraftAssistant' : undefined
    );
}

// Compose DAB SUAs - New Email
export const composeNewHelpWrite: Suggestion = {
    displayText: composeNewHelpWriteDisplayText,
    id: 'composeNewHelpWrite',
    onClick: (targetWindow: Window, mailboxInfo: MailboxInfo) =>
        executePromptWithAgent(
            loc(composeNewHelpWriteCommandText),
            COMPOSE_NEW_HELP_WRITE_CMM_ID,
            targetWindow,
            mailboxInfo
        ),
};

export const composeNewFindRecipients: Suggestion = {
    displayText: composeNewFindRecipientsDisplayText,
    id: 'composeNewFindRecipients',
    onClick: (targetWindow: Window, mailboxInfo: MailboxInfo) =>
        executePromptWithAgent(
            loc(composeNewFindRecipientsCommandText),
            COMPOSE_NEW_FIND_RECIPIENTS_CMM_ID,
            targetWindow,
            mailboxInfo
        ),
};

export const composeNewSupportStatement: Suggestion = {
    displayText: composeNewSupportStatementDisplayText,
    id: 'composeNewSupportStatement',
    onClick: (targetWindow: Window, mailboxInfo: MailboxInfo) =>
        executePromptWithAgent(
            loc(composeNewSupportStatementCommandText),
            COMPOSE_NEW_SUPPORT_STATEMENT_CMM_ID,
            targetWindow,
            mailboxInfo
        ),
};

// Compose DAB SUAs - Reply
export const composeReplyHelpReply: Suggestion = {
    displayText: composeReplyHelpReplyDisplayText,
    id: 'composeReplyHelpReply',
    onClick: (targetWindow: Window, mailboxInfo: MailboxInfo) =>
        executePromptWithAgent(
            loc(composeReplyHelpReplyCommandText),
            COMPOSE_REPLY_HELP_REPLY_CMM_ID,
            targetWindow,
            mailboxInfo
        ),
};

export const composeReplyNextSteps: Suggestion = {
    displayText: composeReplyNextStepsDisplayText,
    id: 'composeReplyNextSteps',
    onClick: (targetWindow: Window, mailboxInfo: MailboxInfo) =>
        executePromptWithAgent(
            loc(composeReplyNextStepsCommandText),
            COMPOSE_REPLY_NEXT_STEPS_CMM_ID,
            targetWindow,
            mailboxInfo
        ),
};

export const composeReplyKeyPoints: Suggestion = {
    displayText: composeReplyKeyPointsDisplayText,
    id: 'composeReplyKeyPoints',
    onClick: (targetWindow: Window, mailboxInfo: MailboxInfo) =>
        executePromptWithAgent(
            loc(composeReplyKeyPointsCommandText),
            COMPOSE_REPLY_KEY_POINTS_CMM_ID,
            targetWindow,
            mailboxInfo
        ),
};

// Compose DAB SUAs - Forward
export const composeForwardExplain: Suggestion = {
    displayText: composeForwardExplainDisplayText,
    id: 'composeForwardExplain',
    onClick: (targetWindow: Window, mailboxInfo: MailboxInfo) =>
        executePromptWithAgent(
            loc(composeForwardExplainCommandText),
            COMPOSE_FORWARD_EXPLAIN_CMM_ID,
            targetWindow,
            mailboxInfo
        ),
};

export const composeForwardWithContext: Suggestion = {
    displayText: composeForwardWithContextDisplayText,
    id: 'composeForwardWithContext',
    onClick: (targetWindow: Window, mailboxInfo: MailboxInfo) =>
        executePromptWithAgent(
            loc(composeForwardWithContextCommandText),
            COMPOSE_FORWARD_WITH_CONTEXT_CMM_ID,
            targetWindow,
            mailboxInfo
        ),
};

export const composeForwardWithTasks: Suggestion = {
    displayText: composeForwardWithTasksDisplayText,
    id: 'composeForwardWithTasks',
    onClick: (targetWindow: Window, mailboxInfo: MailboxInfo) =>
        executePromptWithAgent(
            loc(composeForwardWithTasksCommandText),
            COMPOSE_FORWARD_WITH_TASKS_CMM_ID,
            targetWindow,
            mailboxInfo
        ),
};

// Ensures that one of either `displayText` or `displayTextLocalized` is defined
// TODO: Eventually, using only `displayText: LocalizedString` would be ideal
export type Suggestion =
    | (BaseSuggestion & {
          displayText: ResourceId;
          displayTextLocalized?: never;
      })
    | (BaseSuggestion & {
          displayText?: never;
          displayTextLocalized: LocalizedString | string;
      });

type NonEmptyArray<T> = [T, ...T[]];

export type Suggestions = NonEmptyArray<Suggestion> | undefined;

export const findQuickWins: Suggestion = {
    displayText: findQuickWinsDisplayText,
    id: 'findQuickWins',
    onClick: (targetWindow: Window) =>
        executePrompt(loc(findQuickWinsCommandText), 'cmms7bt06i3', targetWindow),
};

export const getOriented: Suggestion = {
    displayText: getOrientedDisplayText,
    id: 'getOriented',
    onClick: (targetWindow: Window) =>
        executePrompt(loc(getOrientedCommandText), 'cmmmljyih87', targetWindow),
};

export const suggestNextSteps: Suggestion = {
    displayText: suggestNextStepsDisplayText,
    id: 'suggestNextSteps',
    onClick: (targetWindow: Window, mailboxInfo: MailboxInfo) =>
        executePromptWithQueryAnnotation(
            loc(suggestNextStepsCommandText),
            'cmmnga5qm2r',
            targetWindow,
            mailboxInfo
        ),
};

export const summarize: Suggestion = {
    displayText: summarizeDisplayText,
    id: 'summarize',
    onClick: (targetWindow: Window, mailboxInfo: MailboxInfo) =>
        executePromptWithQueryAnnotation(
            loc(summarizeCommandText),
            'cmmemdb07yk',
            targetWindow,
            mailboxInfo
        ),
};

export const summarizeAndReply: Suggestion = {
    displayText: summarizeAndReplyDisplayText,
    id: 'summarizeAndReply',
    onClick: (targetWindow: Window, mailboxInfo: MailboxInfo) =>
        executePromptWithQueryAnnotation(
            loc(summarizeAndReplyCommandText),
            'cmm9uaofbq8',
            targetWindow,
            mailboxInfo
        ),
};

export const triageMyInbox: Suggestion = {
    displayText: triageMyInboxDisplayText,
    id: 'triageMyInbox',
    onClick: (targetWindow: Window) =>
        executePrompt(loc(triageMyInboxCommandText), 'cmm2syst0oj', targetWindow),
};

export const showKeyTakeaways: Suggestion = {
    displayText: showKeyTakeawaysDisplayText,
    id: 'showKeyTakeaways',
    onClick: (targetWindow: Window, mailboxInfo: MailboxInfo) =>
        executePromptWithQueryAnnotation(
            loc(showKeyTakeawaysCommandText),
            'cmmnaejze13',
            targetWindow,
            mailboxInfo
        ),
};

export const showRelatedResources: Suggestion = {
    displayText: showRelatedResourcesDisplayText,
    id: 'showRelatedResources',
    onClick: (targetWindow: Window, mailboxInfo: MailboxInfo) =>
        executePromptWithQueryAnnotation(
            loc(showRelatedResourcesCommandText),
            'cmmsh5so9e9',
            targetWindow,
            mailboxInfo
        ),
};

export const needsUrgentReplies: Suggestion = {
    displayText: needsUrgentRepliesDisplayText,
    id: 'needsUrgentReplies',
    onClick: (targetWindow: Window, mailboxInfo: MailboxInfo) =>
        executePromptWithQueryAnnotation(
            loc(needsUrgentRepliesCommandText),
            'cmmh7ruakpo',
            targetWindow,
            mailboxInfo
        ),
};

export const showTopActionItems: Suggestion = {
    displayText: showTopActionItemsDisplayText,
    id: 'showTopActionItems',
    onClick: (targetWindow: Window) =>
        executePrompt(loc(showTopActionItemsCommandText), 'cmmn6jg6pjz', targetWindow),
};

export const latestOnTopic: Suggestion = {
    displayText: latestOnTopicDisplayText,
    id: 'latestOnTopic',
    onClick: (targetWindow: Window) =>
        executePrompt(loc(latestOnTopicCommandText), 'cmmo9ia57a5', targetWindow),
};

export const whatToGetToFirst: Suggestion = {
    displayText: whatToGetToFirstDisplayText,
    id: 'whatToGetToFirst',
    onClick: (targetWindow: Window) =>
        executePrompt(loc(whatToGetToFirstCommandText), 'cmmnr4v9nfp', targetWindow),
};

export const showMyTopThreeEmails: Suggestion = {
    displayText: showMyTopThreeEmailsDisplayText,
    id: 'showMyTopThreeEmails',
    onClick: (targetWindow: Window) =>
        executePrompt(loc(showMyTopThreeEmailsCommandText), 'cmm9byc18nf', targetWindow),
};
